import React, { useState } from 'react';
import {
  Table,
  Tag,
  Button,
  Space,
  Tooltip,
  Popconfirm,
  Badge,
  Typography,
  Switch,
  InputNumber,
  message,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  LockOutlined,
  UnlockOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  GiftOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminQuest, adminApi } from '../lib/api';

const { Text } = Typography;

interface QuestListProps {
  quests: AdminQuest[];
  isLoading?: boolean;
  onEdit: (quest: AdminQuest) => void;
  onRefresh: () => void;
}

const QUEST_TYPE_COLORS: Record<string, string> = {
  action: 'blue',
  count: 'purple',
  visit: 'cyan',
};

const ACTION_TYPE_LABELS: Record<string, string> = {
  add_item: '➕ Add Item',
  try_on: '👗 Try On',
  chat_ai_stylist: '💬 Chat AI',
  get_recommendation: '✨ Get Rec.',
  upload_photo_model: '📷 Photo Model',
  visit_homepage: '🏠 Homepage',
  visit_closet: '👚 Closet',
  visit_ai_stylist: '🤖 AI Stylist',
  visit_recommendations: '🎯 Recs',
  complete_profile: '👤 Profile',
  view_looks: '👁️ Looks',
};

const TIER_COLORS: Record<string, string> = {
  free: '#8c8c8c',
  bronze: '#CD7F32',
  silver: '#C0C0C0',
  gold: '#FFD700',
  firooze: '#40E0D0',
};

const QuestList: React.FC<QuestListProps> = ({ quests, isLoading, onEdit, onRefresh }) => {
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
  const [tempOrder, setTempOrder] = useState<number>(0);
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (questId: string) => adminApi.deleteQuest(questId),
    onSuccess: () => {
      message.success('Quest deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['admin-quests'] });
      onRefresh();
    },
    onError: (error: any) => {
      message.error(`Failed to delete quest: ${error.message}`);
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ questId, isActive }: { questId: string; isActive: boolean }) =>
      adminApi.updateQuest(questId, { is_active: isActive }),
    onSuccess: (_, { isActive }) => {
      message.success(`Quest ${isActive ? 'activated' : 'deactivated'}`);
      queryClient.invalidateQueries({ queryKey: ['admin-quests'] });
      onRefresh();
    },
    onError: (error: any) => {
      message.error(`Failed to update quest: ${error.message}`);
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (questOrders: { quest_id: string; order: number }[]) =>
      adminApi.reorderQuests(questOrders),
    onSuccess: () => {
      message.success('Quest order updated');
      queryClient.invalidateQueries({ queryKey: ['admin-quests'] });
      onRefresh();
      setEditingOrderId(null);
    },
    onError: (error: any) => {
      message.error(`Failed to reorder: ${error.message}`);
    },
  });

  const handleOrderSave = (questId: string) => {
    reorderMutation.mutate([{ quest_id: questId, order: tempOrder }]);
  };

  const handleMoveUp = (quest: AdminQuest) => {
    const sorted = [...quests].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((q) => q.quest_id === quest.quest_id);
    if (idx <= 0) return;
    const prev = sorted[idx - 1];
    reorderMutation.mutate([
      { quest_id: quest.quest_id, order: prev.order },
      { quest_id: prev.quest_id, order: quest.order },
    ]);
  };

  const handleMoveDown = (quest: AdminQuest) => {
    const sorted = [...quests].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((q) => q.quest_id === quest.quest_id);
    if (idx >= sorted.length - 1) return;
    const next = sorted[idx + 1];
    reorderMutation.mutate([
      { quest_id: quest.quest_id, order: next.order },
      { quest_id: next.quest_id, order: quest.order },
    ]);
  };

  const renderRewards = (quest: AdminQuest) => {
    const rewards = [];
    if (quest.reward_zar_credits) {
      rewards.push(
        <Tag key="zar" color="green" icon={<GiftOutlined />}>
          {quest.reward_zar_credits} ZAR
        </Tag>
      );
    }
    if (quest.reward_free_subscription_days) {
      rewards.push(
        <Tag key="days" color="blue">
          +{quest.reward_free_subscription_days}d
        </Tag>
      );
    }
    if (quest.reward_subscription_tier) {
      rewards.push(
        <Tag
          key="tier"
          style={{
            color: TIER_COLORS[quest.reward_subscription_tier] || '#1890ff',
            borderColor: TIER_COLORS[quest.reward_subscription_tier] || '#1890ff',
          }}
        >
          {quest.reward_subscription_tier.charAt(0).toUpperCase() +
            quest.reward_subscription_tier.slice(1)}
        </Tag>
      );
    }
    if (quest.reward_actions && quest.reward_actions.length > 0) {
      rewards.push(
        <Tooltip key="actions" title={quest.reward_actions.join(', ')}>
          <Tag color="cyan">⚡ {quest.reward_actions.length} action{quest.reward_actions.length > 1 ? 's' : ''}</Tag>
        </Tooltip>
      );
    }
    if (quest.claim_bottomsheet_enabled) {
      rewards.push(
        <Tooltip key="bs" title="Custom claim bottomsheet configured">
          <Tag color="gold">🎁 Bottomsheet</Tag>
        </Tooltip>
      );
    }
    if (quest.help_video_key) {
      rewards.push(
        <Tooltip key="video" title={`Animated tutorial: "${quest.help_video_key}"`}>
          <Tag color="volcano">📹 Tutorial</Tag>
        </Tooltip>
      );
    }
    return rewards.length > 0 ? <Space size={2} wrap>{rewards}</Space> : <Text type="secondary">—</Text>;
  };

  const sortedQuests = [...quests].sort((a, b) => a.order - b.order);

  const columns = [
    {
      title: 'Order',
      dataIndex: 'order',
      key: 'order',
      width: 120,
      render: (order: number, record: AdminQuest) => {
        const sorted = [...quests].sort((a, b) => a.order - b.order);
        const idx = sorted.findIndex((q) => q.quest_id === record.quest_id);
        const isFirst = idx === 0;
        const isLast = idx === sorted.length - 1;

        if (editingOrderId === record.quest_id) {
          return (
            <Space>
              <InputNumber
                size="small"
                value={tempOrder}
                onChange={(v) => setTempOrder(v ?? order)}
                min={0}
                style={{ width: 60 }}
              />
              <Button
                size="small"
                type="primary"
                onClick={() => handleOrderSave(record.quest_id)}
                loading={reorderMutation.isPending}
              >
                Save
              </Button>
              <Button size="small" onClick={() => setEditingOrderId(null)}>
                ✕
              </Button>
            </Space>
          );
        }

        return (
          <Space>
            <Text
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => {
                setEditingOrderId(record.quest_id);
                setTempOrder(order);
              }}
            >
              {order}
            </Text>
            <Button
              size="small"
              type="text"
              icon={<ArrowUpOutlined />}
              disabled={isFirst || reorderMutation.isPending}
              onClick={() => handleMoveUp(record)}
            />
            <Button
              size="small"
              type="text"
              icon={<ArrowDownOutlined />}
              disabled={isLast || reorderMutation.isPending}
              onClick={() => handleMoveDown(record)}
            />
          </Space>
        );
      },
    },
    {
      title: 'Quest',
      key: 'quest',
      render: (_: any, record: AdminQuest) => (
        <Space direction="vertical" size={2}>
          <Space>
            <Text strong>{record.title}</Text>
            {record.is_onboarding && <Tag color="orange">Onboarding</Tag>}
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.quest_id}
          </Text>
          <Text style={{ fontSize: 12, color: '#666', maxWidth: 250, display: 'block' }}>
            {record.description}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Type',
      key: 'type',
      width: 160,
      render: (_: any, record: AdminQuest) => {
        const isCompound =
          Array.isArray(record.action_requirements) && record.action_requirements.length > 0;
        return (
          <Space direction="vertical" size={2}>
            <Tag color={QUEST_TYPE_COLORS[record.quest_type] || 'default'}>
              {record.quest_type.toUpperCase()}
            </Tag>
            {isCompound ? (
              <Tooltip
                title={record.action_requirements
                  .map((r) => `${ACTION_TYPE_LABELS[r.action_type] || r.action_type} ×${r.target_count}`)
                  .join(' + ')}
              >
                <Tag color="purple">
                  🔗 Compound ({record.action_requirements.length} actions)
                </Tag>
              </Tooltip>
            ) : record.action_type ? (
              <Tag>{ACTION_TYPE_LABELS[record.action_type] || record.action_type}</Tag>
            ) : null}
            {!isCompound && record.quest_type === 'count' && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                Target: {record.target_count}×
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Rewards',
      key: 'rewards',
      width: 180,
      render: (_: any, record: AdminQuest) => renderRewards(record),
    },
    {
      title: 'Prerequisites',
      key: 'prerequisites',
      width: 140,
      render: (_: any, record: AdminQuest) => {
        if (!record.prerequisite_quests || record.prerequisite_quests.length === 0) {
          return <Text type="secondary">—</Text>;
        }
        return (
          <Tooltip title={record.prerequisite_quests.join(', ')}>
            <Badge count={record.prerequisite_quests.length} color="geekblue">
              <LockOutlined style={{ fontSize: 16, color: '#1890ff' }} />
            </Badge>
          </Tooltip>
        );
      },
    },
    {
      title: 'Active',
      key: 'is_active',
      width: 80,
      render: (_: any, record: AdminQuest) => (
        <Switch
          checked={record.is_active}
          size="small"
          loading={toggleActiveMutation.isPending}
          onChange={(checked) =>
            toggleActiveMutation.mutate({ questId: record.quest_id, isActive: checked })
          }
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: any, record: AdminQuest) => (
        <Space>
          <Tooltip title="Edit Quest">
            <Button
              size="small"
              type="text"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this quest?"
            description="This will soft-delete the quest. User progress will be preserved."
            onConfirm={() => deleteMutation.mutate(record.quest_id)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete Quest">
              <Button
                size="small"
                type="text"
                danger
                icon={<DeleteOutlined />}
                loading={deleteMutation.isPending}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Table
      dataSource={sortedQuests}
      columns={columns}
      rowKey="quest_id"
      loading={isLoading}
      pagination={{ pageSize: 20, showSizeChanger: true }}
      size="middle"
      scroll={{ x: 900 }}
      rowClassName={(record) => (!record.is_active ? 'row-inactive' : '')}
    />
  );
};

export default QuestList;

